import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as api from '../lib/api';
import { formatApiError } from '../lib/format';

// The answer never differs by whether the address has an account, so this screen
// holds no branching on the response at all: one submit, one confirmation.
export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      await api.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="page signin-page" data-testid="forgot-password-page">
        <div className="signin-content">
          <div className="signin-header">
            <span className="welcome-brand">irecommend</span>
            <h1 className="signin-title">Check your inbox</h1>
            <p className="signin-subtitle" data-testid="forgot-password-sent">
              If that email address has an irecommend account, we have sent a reset link.
              It expires in 60 minutes and can only be used once.
            </p>
          </div>
          <button
            type="button"
            className="signin-btn signin-btn-standalone"
            onClick={() => navigate('/signin')}
            data-testid="forgot-password-back-to-signin"
          >
            Back to sign in
          </button>
          <p className="signin-note">
            Nothing arrived? Check your spam folder, or{' '}
            <button
              className="signin-toggle"
              onClick={() => { setSent(false); setError(''); }}
              data-testid="forgot-password-try-again"
            >
              try another email address
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page signin-page" data-testid="forgot-password-page">
      <div className="signin-content">
        <div className="signin-header">
          <span className="welcome-brand">irecommend</span>
          <h1 className="signin-title">Reset your password</h1>
          <p className="signin-subtitle">
            Enter the email address you use for irecommend and we will send you a reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signin-form">
          <div className="signin-field">
            <label className="signin-label" htmlFor="forgotEmail">Email</label>
            <input
              id="forgotEmail"
              type="email"
              className={`signin-input ${error ? 'signin-input-error' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              autoFocus
              data-testid="forgot-password-email"
            />
          </div>

          {error && <span className="signin-error" data-testid="forgot-password-error">{error}</span>}

          <button
            type="submit"
            className="signin-btn"
            disabled={loading}
            data-testid="forgot-password-submit"
          >
            {loading ? 'Please wait…' : 'Send reset link'}
          </button>
        </form>

        <p className="signin-note">
          <Link className="signin-toggle" to="/signin" data-testid="forgot-password-signin-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
